import React, { useState, useEffect } from 'react';
import { Search, Download, Users, Eye, AlertTriangle, CheckCircle2, UserCircle2, Building2 } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { cn } from '../../../components/ui/utils';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { Asset, AssetStatus } from '../../../types/asset';
import { toSydneyDate } from '../../../utils/helpers';

const CurrentAssignmentsReport: React.FC = () => {
    const navigate = useNavigate();
    const [assets, setAssets] = useState<Asset[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('All Types');
    const [assignmentFilter, setAssignmentFilter] = useState('All Assignments');

    useEffect(() => {
        const fetchAssets = async () => {
            try {
                setLoading(true);
                const response = await api.get<Asset[]>(`/api/assets`);
                // Only keep assets that are currently assigned
                const assignedAssets = response.filter(a => a.assignedToUser || a.assignedToDepartment);
                setAssets(assignedAssets);
            } catch (error) {
                console.error('Error fetching assets for assignment report:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAssets();
    }, []);

    const filteredAssets = assets.filter(asset => {
        const matchesSearch = asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            asset.tag.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (asset.assignedToUser?.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (asset.assignedToDepartment?.departmentName.toLowerCase().includes(searchQuery.toLowerCase()));

        const matchesType = typeFilter === 'All Types' || asset.type === typeFilter;

        let matchesAssignment = true;
        if (assignmentFilter === 'Assigned to Users') {
            matchesAssignment = !!asset.assignedToUser;
        } else if (assignmentFilter === 'Assigned to Departments') {
            matchesAssignment = !!asset.assignedToDepartment;
        }

        return matchesSearch && matchesType && matchesAssignment;
    });

    const totalAssignments = filteredAssets.length;
    const userAssignmentsCount = filteredAssets.filter(a => a.assignedToUser).length;
    const deptAssignmentsCount = filteredAssets.filter(a => a.assignedToDepartment).length;

    // Calculate unique users
    const uniqueUsers = new Set();
    filteredAssets.forEach(a => {
        if (a.assignedToUser) uniqueUsers.add(a.assignedToUser.id);
    });
    const uniqueUsersCount = uniqueUsers.size;

    const getStatusStyles = (status: AssetStatus) => {
        switch (status) {
            case 'Assigned': return 'bg-blue-50 text-blue-600 border-blue-100';
            case 'Available': return 'bg-green-50 text-green-600 border-green-100';
            case 'In Maintenance': return 'bg-amber-50 text-amber-600 border-amber-100';
            case 'Needs Repair':
            case 'Damaged': return 'bg-red-50 text-red-600 border-red-100';
            case 'Lost': return 'bg-gray-800 text-white border-gray-900';
            case 'Archived':
            case 'Retired':
            case 'Disposed': return 'bg-gray-100 text-gray-500 border-gray-200';
            default: return 'bg-gray-50 text-gray-600 border-gray-100';
        }
    };

    const getInitials = (name: string) => {
        if (!name) return '??';
        return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    };

    const downloadCSV = () => {
        const headers = ['Asset Name', 'Asset ID', 'Asset Type', 'Assigned To', 'Assignment Type', 'Assigned By', 'Assignment Date', 'Asset Status', 'Notes'];
        const rows = filteredAssets.map(asset => {
            const activeAssignment = asset.assignments?.find(a => a.isActive);
            return [
                asset.name,
                asset.tag,
                asset.type,
                asset.assignedToUser?.name || asset.assignedToDepartment?.departmentName || '-',
                asset.assignedToUser ? 'User' : asset.assignedToDepartment ? 'Department' : '-',
                activeAssignment?.assignedBy?.name || '-',
                toSydneyDate(activeAssignment?.assignedDate) || '-',
                asset.status,
                activeAssignment?.notes || '-'
            ];
        });

        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `current-assignments-report-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden animate-in fade-in duration-500">
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-50 rounded-lg">
                            <Users size={20} className="text-gray-600" />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 text-lg">Current Assignments Report</h3>
                            <p className="text-gray-500 text-sm font-medium">Who is responsible for what - Current accountability tracking</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" className="border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2" onClick={downloadCSV}>
                            <Download size={16} />
                            <span>CSV</span>
                        </Button>
                    </div>
                </div>

                {/* Filters Row */}
                <div className="mt-6 flex flex-col sm:flex-row items-center gap-4">
                    <div className="relative w-full flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <Input
                            placeholder="Search assignments..."
                            className="pl-10 bg-gray-50/50 border-gray-100 focus:bg-white transition-all rounded-xl h-11 text-sm font-medium w-full"
                            value={searchQuery}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center gap-3 w-full sm:w-auto overflow-x-auto no-scrollbar">
                        <select
                            className="h-11 px-4 text-center bg-gray-50/50 border border-gray-100 rounded-xl text-sm font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 w-full sm:w-40 appearance-none bg-no-repeat bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%20%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M5%207.5L10%2012.5L15%207.5%22%20stroke%3D%22%239CA3AF%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%2F%3E%3C%2Fsvg%3E')] bg-[position:right_10px_center] cursor-pointer"
                            value={typeFilter}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTypeFilter(e.target.value)}
                        >
                            <option>All Types</option>
                            <option>Vehicle</option>
                            <option>Plant & Equipment</option>
                            <option>Tools</option>
                            <option>IT Equipment</option>
                            <option>Mobile Devices</option>
                            <option>Lifting/Slings</option>
                            <option>Furniture</option>
                        </select>
                        <select
                            className="h-11 px-4 text-center bg-gray-50/50 border border-gray-100 rounded-xl text-sm font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 w-full sm:w-48 appearance-none bg-no-repeat bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%20%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M5%207.5L10%2012.5L15%207.5%22%20stroke%3D%22%239CA3AF%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%2F%3E%3C%2Fsvg%3E')] bg-[position:right_10px_center] cursor-pointer"
                            value={assignmentFilter}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAssignmentFilter(e.target.value)}
                        >
                            <option>All Assignments</option>
                            <option>Assigned to Users</option>
                            <option>Assigned to Departments</option>
                        </select>
                    </div>
                </div>

                {/* Stats Row */}
                <div className="mt-6 bg-gray-50 rounded-2xl p-6 flex flex-wrap items-center justify-between gap-6">
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Total Assignments</p>
                        <h3 className="text-2xl font-black text-gray-900">{totalAssignments}</h3>
                    </div>
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">User Assignments</p>
                        <h3 className="text-2xl font-black text-gray-900">{userAssignmentsCount}</h3>
                    </div>
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Dept Assignments</p>
                        <h3 className="text-2xl font-black text-gray-900">{deptAssignmentsCount}</h3>
                    </div>
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Unique Users</p>
                        <h3 className="text-2xl font-black text-gray-900">{uniqueUsersCount}</h3>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto no-scrollbar pb-4">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="bg-white border-b border-gray-100">
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Asset</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Asset Type</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Assigned To</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Assignment Type</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Assigned By</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Assignment Date</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Asset Status</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Notes</th>
                            <th className="px-6 py-4 text-right text-[11px] font-bold text-gray-800 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading ? (
                            [...Array(3)].map((_, i) => (
                                <tr key={i} className="animate-pulse">
                                    <td colSpan={9} className="px-6 py-4"><div className="h-10 bg-gray-50 rounded-xl w-full" /></td>
                                </tr>
                            ))
                        ) : filteredAssets.length === 0 ? (
                            <tr>
                                <td colSpan={9} className="px-6 py-12 text-center text-gray-400">
                                    No assignments found matching the criteria.
                                </td>
                            </tr>
                        ) : (
                            filteredAssets.map((asset) => {
                                const activeAssignment = asset.assignments?.find(a => a.isActive);

                                return (
                                    <tr key={asset.id} className="hover:bg-gray-50/50 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="min-w-[160px]">
                                                <p className="font-bold text-gray-900 text-[13px]">{asset.name}</p>
                                                <p className="text-[11px] font-medium text-gray-400 font-mono tracking-wider mt-0.5">{asset.tag}</p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-[11px] font-bold text-gray-600 border border-gray-200 rounded-md px-2.5 py-1 whitespace-nowrap bg-white">{asset.type}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            {asset.assignedToUser ? (
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center text-[10px] shrink-0">
                                                        {getInitials(asset.assignedToUser.name)}
                                                    </div>
                                                    <div className="min-w-[120px]">
                                                        <p className="font-bold text-gray-900 text-[13px] whitespace-nowrap">{asset.assignedToUser.name}</p>
                                                        <p className="text-[11px] text-gray-500 whitespace-nowrap">{asset.assignedToUser.jobRole || 'No role'}</p>
                                                    </div>
                                                </div>
                                            ) : asset.assignedToDepartment ? (
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 font-bold flex items-center justify-center text-[10px] shrink-0">
                                                        {getInitials(asset.assignedToDepartment.departmentName)}
                                                    </div>
                                                    <div className="min-w-[120px]">
                                                        <p className="font-bold text-gray-900 text-[13px] whitespace-nowrap">{asset.assignedToDepartment.departmentName}</p>
                                                        <p className="text-[11px] text-gray-500 whitespace-nowrap">Department</p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            {asset.assignedToUser ? (
                                                <Badge variant="outline" className="bg-slate-900 text-white hover:bg-slate-800 border-0 flex items-center gap-1.5 w-fit px-2 py-1">
                                                    <UserCircle2 size={12} />
                                                    <span className="text-[10px]">User</span>
                                                </Badge>
                                            ) : asset.assignedToDepartment ? (
                                                <Badge variant="outline" className="bg-purple-100 text-purple-700 hover:bg-purple-200 border-0 flex items-center gap-1.5 w-fit px-2 py-1">
                                                    <Building2 size={12} />
                                                    <span className="text-[10px]">Dept</span>
                                                </Badge>
                                            ) : '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            {activeAssignment && activeAssignment.assignedBy ? (
                                                <div className="flex items-center gap-2">
                                                    <div className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 font-bold flex items-center justify-center text-[9px] shrink-0">
                                                        {getInitials(activeAssignment.assignedBy.name)}
                                                    </div>
                                                    <span className="text-[13px] font-medium text-gray-700 whitespace-nowrap">{activeAssignment.assignedBy.name}</span>
                                                </div>
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap">
                                            {toSydneyDate(activeAssignment?.assignedDate) || '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <Badge variant="outline" className={cn("rounded-lg border-0 font-bold px-2 py-1 text-[11px] whitespace-nowrap", getStatusStyles(asset.status))}>
                                                {asset.status === 'Needs Repair' ? (
                                                    <span className="flex items-center gap-1.5">
                                                        <AlertTriangle size={12} /> Needs Repair
                                                    </span>
                                                ) : (
                                                    <span>{asset.status}</span>
                                                )}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4">
                                            {activeAssignment && activeAssignment.notes ? (
                                                <span className="text-[11px] text-gray-500 max-w-[150px] truncate block" title={activeAssignment.notes}>
                                                    {activeAssignment.notes}
                                                </span>
                                            ) : <span className="text-gray-400">-</span>}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="w-8 h-8 rounded-lg text-gray-400 hover:text-gray-900 transition-colors"
                                                onClick={() => navigate(`/assets/${asset.id}`)}
                                            >
                                                <Eye size={16} />
                                            </Button>
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CurrentAssignmentsReport;
